import React, { useState, useEffect } from "react";
import useDeviceType from "../../shared/hooks/useDeviceType";
import { DeviceTypes } from "../../shared/utils/enums";
import SideMenu from "./sideMenu";
import { CustomLayoutProps } from "../../interfaces/customLayout";
import { Menu, X } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useLocation } from "react-router-dom";
import { getCategoriesConfig } from "../content/genioMD/actions";
import { getTabs } from "../content/drugsAndDiseases/actions";
import { PageInfo, titleService } from "../../shared/services/titleService";
import { getPageInfoFromPath } from "../../shared/utils/titleHelper";

const CustomLayout: React.FC<CustomLayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<PageInfo | null>(null);
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.mobile;
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const isQARoute = location.pathname.includes("/qa");
  const noPaddingRoutes = ["/drugs-and-diseases"];

  // Fetch categories on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getCategoriesConfig();
        await getTabs();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (!!currentUser) {
      fetchData();
    }
  }, [currentUser]);

  useEffect(() => {
    // Default title logic from path
    const { currentPage: pageInfoFromPath } = getPageInfoFromPath(
      location.pathname,
    );
    setCurrentPage(pageInfoFromPath);
    document.title = pageInfoFromPath?.label || "InsightHub";

    // Subscribe to overrides from pages
    const handlePageInfoChange = (pageInfo: PageInfo | null) => {
      if (pageInfo) {
        setCurrentPage(pageInfo);
        document.title = pageInfo.label;
      } else {
        // When cleared, revert to path-based title
        const { currentPage: pageInfoFromPath } = getPageInfoFromPath(
          location.pathname,
        );
        setCurrentPage(pageInfoFromPath);
        document.title = pageInfoFromPath?.label || "InsightHub";
      }
    };

    const unsubscribe = titleService.subscribe(handlePageInfoChange);

    return () => {
      unsubscribe();
    };
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  if (!currentUser) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <div
          className={`
            ${
              isMobile
                ? `fixed top-0 left-0 h-full w-72 bg-white dark:bg-black z-50 transform transition-transform duration-300 ease-in-out ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}`
                : "bg-white dark:bg-black"
            }
          `}
        >
          <SideMenu
            onLinkClick={closeMenu}
            isCollapsed={!isMobile && isQARoute}
          />
        </div>

        {/* Desktop Header */}
        {!isMobile && (
          <div
            className={`fixed top-0 right-0 h-16 bg-white dark:bg-black border-b border-gray-100 dark:border-gray-900 px-6 flex items-center z-40 transition-all duration-300 ease-in-out ${isQARoute ? "left-16" : "left-72"}`}
          >
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {currentPage?.icon && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-900">
                    <currentPage.icon
                      size={18}
                      className="text-primary dark:text-white"
                    />
                  </div>
                )}
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {currentPage?.label || "InsightHub"}
                </h1>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Header */}
        {isMobile && (
          <div className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-black px-4 flex items-center justify-between z-40">
            <div className="flex items-center space-x-2">
              {currentPage?.icon && (
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-900">
                  <currentPage.icon
                    size={18}
                    className="text-primary dark:text-white"
                  />
                </div>
              )}
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentPage?.label || "InsightHub"}
              </h1>
            </div>
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-400/40 transition-colors duration-200"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        )}

        {/* Mobile Overlay */}
        {isMobile && isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={closeMenu}
          />
        )}

        {/* Main Content */}
        <div
          className={`flex-1 ${isMobile ? "pt-16" : noPaddingRoutes.includes(location.pathname) ? "pt-0" : "pt-16"} overflow-y-auto ${
            noPaddingRoutes.includes(location.pathname) || isQARoute
              ? ""
              : "px-6"
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default CustomLayout;
